import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { GetApplicationInsights } from './applicationInsights';
import { ReactNode, createContext, useEffect, useState, lazy } from 'react';
import { useLazyGetReactConfigQuery } from './configApi';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import ScriveConfirmationPage from './pages/ApplicationV3/pages/ScriveConfirmationPage';
import PartyApplication from './pages/PartyApplication/PartyApplication';

const PublicLayout = lazy(
  () => import('./components/common/PublicLayout/PublicLayout')
);
const PortalLayoutComponent = lazy(
  () => import('./components/common/PortalLayout/PortalLayoutComponent')
);

const FormHome = lazy(() =>
  import('./components/FormHome').then((module) => ({
    default: module.FormHome,
  }))
);
const Completion = lazy(
  () => import('./pages/Application/Completion/Completion')
);
const PrivatePersonApplicationEntry = lazy(
  () => import('./pages/Application/PrivateApplicationEntry')
);
const ErrorPage = lazy(() => import('./pages/ErrorPage/ErrorPage'));
const AdjustmentAid = lazy(
  () => import('./pages/Application/AdjustmenAid/AdjustmentAid')
);
const NewContractSuccessPage = lazy(
  () => import('./pages/Application/NewContract/NewContractSuccessPage')
);
const Home = lazy(() =>
  import('./components/home/Home').then((module) => ({ default: module.Home }))
);
const SignoutPage = lazy(() => import('./pages/Signout/SignoutPage'));
const CsnApplication = lazy(
  () => import('./pages/Application/CsnApplication/CsnApplication')
);
const CsnApplicationSuccessPage = lazy(
  () => import('./pages/Application/CsnApplication/CsnApplicationSuccessPage')
);
const CompletionSuccessPage = lazy(
  () => import('./pages/Application/Completion/CompletionSuccessPage')
);
const Administration = lazy(
  () => import('./pages/Administration/Administration')
);
const ContentPage = lazy(() => import('./pages/Content/ContentPage'));
const PreviewPage = lazy(() => import('./pages/Preview/PreviewPage'));
const News = lazy(() => import('./pages/News/News'));
const Billing = lazy(() => import('./pages/Billing/Billing'));
const ConversionProjectPortalPage = lazy(
  () => import('./pages/ConversionProject/ConversionProjectPortalPage')
);
const CoachPortalPage = lazy(() => import('./pages/Coach/CoachPortalPage'));
const ExternalAdvisorPortalPage = lazy(
  () => import('./pages/ExternalAdvisor/ExternalAdvisorPortalPage')
);
const GuidancePortalPage = lazy(
  () => import('./pages/Guidance/GuidancePortalPage')
);
const Application = lazy(() => import('./pages/ApplicationV3/Application'));

interface PublicLayoutComponentProps {
  children: ReactNode;
  headerText: string;
}

export const ApplicationInsightsContext = createContext<
  ApplicationInsights | undefined
>(undefined);

export const PublicLayoutComponent = ({
  children,
  headerText,
}: PublicLayoutComponentProps) => {
  const [reactPlugin, setReactPlugin] = useState<ReactPlugin | undefined>(
    undefined
  );
  const [appInsights, setAppInsights] = useState<
    ApplicationInsights | undefined
  >(undefined);
  const [loadReactConfig, loadReactConfigResult] = useLazyGetReactConfigQuery();
  const { data: reactConfig, isLoading } = loadReactConfigResult;
  useEffect(() => {
    if (reactConfig === undefined && !isLoading) {
      loadReactConfig();
      setReactPlugin(new ReactPlugin());
    }
  }, [reactConfig, isLoading, loadReactConfig]);

  useEffect(() => {
    if (
      reactConfig !== undefined &&
      reactConfig.applicationInsightsConnectionString !== undefined &&
      !isLoading &&
      appInsights === undefined
    ) {
      setAppInsights(GetApplicationInsights(reactConfig));
    }
  }, [reactConfig, isLoading, appInsights, reactPlugin]);

  if (appInsights === null || appInsights === undefined) {
    return <PublicLayout headerText={headerText}>{children}</PublicLayout>;
  }

  return (
    <AppInsightsErrorBoundary
      onError={() => (
        <ErrorPage header="Något gick fel..">
          <p>Ett oväntat fel har inträffat</p>
        </ErrorPage>
      )}
      appInsights={reactPlugin!}
    >
      <ApplicationInsightsContext.Provider value={appInsights}>
        <PublicLayout headerText={headerText}>{children}</PublicLayout>
      </ApplicationInsightsContext.Provider>
    </AppInsightsErrorBoundary>
  );
};

const AppRoutes = [
  {
    path: '/forms',
    element: <FormHome />,
  },
  {
    index: true,
    element: (
      <PortalLayoutComponent>
        <Home />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/ansokan',
    element: <Application />,
  },
  {
    path: '/ansokan/success',
    element: <ScriveConfirmationPage />,
  },
  {
    path: '/partsansokan',
    element: <PartyApplication />,
  },
  {
    path: '/omstallningsprojekt/:projectKey',
    element: (
      <PortalLayoutComponent>
        <ConversionProjectPortalPage />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/administration',
    element: (
      <PortalLayoutComponent>
        <Administration />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/deltagare/:participantKey',
    element: (
      <PortalLayoutComponent>
        <CoachPortalPage />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/kartlaeggningssamtal/:participantKey',
    element: (
      <PortalLayoutComponent>
        <ExternalAdvisorPortalPage />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/vagledning/:participantKey',
    element: (
      <PortalLayoutComponent>
        <GuidancePortalPage />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/logout',
    element: <SignoutPage />,
  },
  {
    path: '/content/:path',
    element: (
      <PortalLayoutComponent>
        <ContentPage />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/preview/:id',
    element: <PreviewPage />,
  },
  {
    path: '/nyheter',
    element: (
      <PortalLayoutComponent>
        <News />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/nyheter/:id',
    element: (
      <PortalLayoutComponent>
        <News />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/fakturering',
    element: (
      <PortalLayoutComponent>
        <Billing />
      </PortalLayoutComponent>
    ),
  },
  {
    path: '/komplettering',
    element: (
      <PublicLayoutComponent headerText="Formulär för komplettering">
        <Completion />
      </PublicLayoutComponent>
    ),
  },
  {
    path: '/komplettering/success',
    element: <CompletionSuccessPage />,
  },
  {
    path: '/ansokan-privatperson',
    element: <PrivatePersonApplicationEntry />,
  },
  {
    path: 'omstallningsstod',
    element: (
      <PublicLayout headerText="Ansök om omställningsstöd" color="blue">
        <AdjustmentAid />
      </PublicLayout>
    ),
  },
  {
    path: '/ansokan-privatperson/success',
    element: <NewContractSuccessPage />,
  },
  {
    path: '/ansokan-csn',
    element: (
      <PublicLayout headerText="Komplettering till din CSN-ansökan">
        <CsnApplication />
      </PublicLayout>
    ),
  },
  {
    path: '/ansokan-csn/success',
    element: <CsnApplicationSuccessPage />,
  },
];

export default AppRoutes;
